import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import { fetchAnomalies, fetchFeatures, fetchTypes } from "../../lib/api";
import { exportUrl } from "../../lib/csv";
import { formatCoords, formatEventDate, TYPE_LABELS } from "../../lib/format";
import type { Feature, LunarEvent, PhenomenonType, PhenomenonTypeId } from "../../types";

const PAGE_SIZE = 40;

const Catalog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState<LunarEvent[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [types, setTypes] = useState<PhenomenonType[]>([]);
  const [error, setError] = useState<string | null>(null);

  const q = searchParams.get("q") ?? "";
  const typeFilter = (searchParams.get("type") ?? "") as PhenomenonTypeId | "";
  const featureId = searchParams.get("feature") ?? "";
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchAnomalies({ pageSize: 1000 }),
      fetchFeatures(),
      fetchTypes(),
    ])
      .then(([anomalyPage, featureList, typeList]) => {
        if (cancelled) return;
        setEvents(anomalyPage.items);
        setFeatures(featureList);
        setTypes(typeList);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return events.filter((event) => {
      if (typeFilter && event.phenomenonTypeId !== typeFilter) return false;
      if (featureId && event.featureId !== featureId) return false;
      if (!query) return true;
      const hay = [
        event.description,
        event.locationText,
        event.featureName,
        event.observer,
        event.reference,
        event.dateText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(query);
    });
  }, [events, q, typeFilter, featureId]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE,
  );

  const update = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    if (!("page" in patch)) {
      next.delete("page");
    }
    setSearchParams(next);
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 py-12 w-full">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-4xl font-bold text-alt-blk mb-2">Catalog</h1>
            <p className="text-alt-blk/70">
              {filtered.length.toLocaleString()} reports from NASA TR R-277 (Middlehurst et al. 1968).
            </p>
          </div>
          {filtered.length > 0 ? (
            <a
              href={exportUrl({
                q: q.trim() || undefined,
                type: typeFilter || undefined,
                featureId: featureId || undefined,
              })}
              className="inline-block bg-mars hover:bg-sunset text-white px-6 py-3 rounded-2xl font-semibold text-center whitespace-nowrap"
            >
              Export CSV
            </a>
          ) : (
            <span className="inline-block bg-mars/40 text-white px-6 py-3 rounded-2xl font-semibold text-center whitespace-nowrap cursor-not-allowed">
              Export CSV
            </span>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <input
            type="search"
            value={q}
            onChange={(e) => update({ q: e.target.value })}
            placeholder="Search the archive…"
            className="rounded-xl border border-alt-blk/15 bg-white px-4 py-3 md:col-span-1"
          />
          <select
            value={typeFilter}
            onChange={(e) => update({ type: e.target.value })}
            className="rounded-xl border border-alt-blk/15 bg-white px-4 py-3"
          >
            <option value="">All types</option>
            {types.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
          <select
            value={featureId}
            onChange={(e) => update({ feature: e.target.value })}
            className="rounded-xl border border-alt-blk/15 bg-white px-4 py-3"
          >
            <option value="">All features</option>
            {features.map((feature) => (
              <option key={feature.id} value={feature.id}>
                {feature.name} ({feature.eventCount})
              </option>
            ))}
          </select>
        </div>

        {error && <p className="text-mars mb-6">{error}</p>}

        <div className="overflow-x-auto rounded-2xl border border-alt-blk/10 bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-alt-blk text-alt-wht text-left">
              <tr>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Feature</th>
                <th className="px-4 py-3 font-semibold">Type</th>
                <th className="px-4 py-3 font-semibold">Description</th>
                <th className="px-4 py-3 font-semibold">Observer</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map((event) => (
                <tr key={event.id} className="border-t border-alt-blk/10 hover:bg-alt-wht/60">
                  <td className="px-4 py-3 whitespace-nowrap align-top">
                    <Link to={`/events/${event.id}`} className="text-turquoise hover:text-mars font-medium">
                      {formatEventDate(event)}
                    </Link>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div>{event.featureName ?? event.locationText}</div>
                    <div className="text-xs text-alt-blk/50">
                      {formatCoords(event.latitude, event.longitude)}
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top whitespace-nowrap">
                    {TYPE_LABELS[event.phenomenonTypeId]}
                  </td>
                  <td className="px-4 py-3 align-top max-w-xl">{event.description}</td>
                  <td className="px-4 py-3 align-top">{event.observer ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between mt-6 text-sm">
          <button
            disabled={currentPage <= 1}
            onClick={() => update({ page: String(currentPage - 1) })}
            className="px-4 py-2 rounded-xl bg-alt-blk text-white disabled:opacity-40"
          >
            Previous
          </button>
          <span>
            Page {currentPage} of {pageCount}
          </span>
          <button
            disabled={currentPage >= pageCount}
            onClick={() => update({ page: String(currentPage + 1) })}
            className="px-4 py-2 rounded-xl bg-alt-blk text-white disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </section>
    </Layout>
  );
};

export default Catalog;
